import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { AuthLayout } from '../../components/layouts/AuthLayout';
import { slideUp } from '@/lib/motion';

/**
 * Workspace picker shown after a login that spans multiple memberships
 * (`requires_tenant_selection`). Selecting a workspace calls `selectTenant`
 * which promotes the pre-auth session into an active membership; we then drop
 * the member onto the smart home, which lands them in their resolved view mode.
 */
const SelectWorkspacePage: React.FC = () => {
  const { pendingMemberships, selectTenant } = useAuth();
  const navigate = useNavigate();
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // No pending selection (direct nav or refresh that dropped the pre-auth
  // session) → bounce back to login.
  useEffect(() => {
    if (!pendingMemberships) {
      navigate('/auth/login', { replace: true });
    }
  }, [pendingMemberships, navigate]);

  if (!pendingMemberships) {
    return null;
  }

  const handleSelect = async (tenantId: string) => {
    setError(null);
    setSelectingId(tenantId);
    try {
      await selectTenant(tenantId);
      navigate('/', { replace: true });
    } catch {
      setError('Could not open that workspace. Please try again.');
      setSelectingId(null);
    }
  };

  return (
    <AuthLayout>
      <motion.div {...slideUp}>
        <div className="mb-8">
          <h1 className="text-[26px] font-bold text-text-primary mb-2">
            Choose a Workspace
          </h1>
          <p className="text-sm text-text-secondary">
            You belong to more than one workspace. Pick one to continue.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3.5 bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400 rounded-lg text-sm">
            {error}
          </div>
        )}

        <ul className="space-y-2">
          {pendingMemberships.map((m) => (
            <li key={m.membership_id}>
              <button
                type="button"
                disabled={selectingId !== null}
                className="w-full flex items-center justify-between text-left rounded-full border border-border-primary/40 p-4 transition-colors hover:bg-surface-elevated disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => handleSelect(m.tenant.id)}
              >
                <span className="flex flex-col">
                  <span className="font-medium text-text-primary">{m.tenant.name}</span>
                  <span className="text-text-secondary text-sm">{m.role}</span>
                </span>
                {selectingId === m.tenant.id && (
                  <span className="spinner text-primary-600 dark:text-primary-350" aria-label="Opening workspace..." />
                )}
              </button>
            </li>
          ))}
        </ul>
      </motion.div>
    </AuthLayout>
  );
};

export default SelectWorkspacePage;
